import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { Button } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import {
  getUndoPreview,
  undoMigration,
  MigracionApiError,
  type LoteInfo,
  type UndoBlock,
  type UndoPreview,
} from '@/infrastructure/api/migracionFrontmyApi';

const K = 'pages.migracionFrontmy.undo';

function errorText(err: unknown): string {
  if (err instanceof MigracionApiError && err.codigo) {
    return i18next.t(`pages.migracionFrontmy.apiErrors.${err.codigo}`, { defaultValue: err.message || err.codigo });
  }
  return err instanceof Error && err.message ? err.message : i18next.t('pages.migracionFrontmy.apiErrors.generic');
}

function when(iso: string): string {
  return new Date(iso).toLocaleString(i18next.language, { dateStyle: 'short', timeStyle: 'short' });
}

const BlockList: React.FC<{ bloqueos: UndoBlock[] }> = ({ bloqueos }) => {
  const { t } = useTranslation();
  return (
    <div className="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
      <p className="font-semibold">{t(`${K}.blockedTitle`)}</p>
      <ul className="mt-1 list-disc space-y-0.5 pl-5">
        {bloqueos.map((b, i) => (
          <li key={i}>
            {t(`${K}.blocks.${b.codigo}`, { defaultValue: b.codigo, count: b.cantidad, table: b.tabla ?? '' })}
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Paso a paso del deshacer: ver qué se borra → escribir el nombre de la clínica → confirmar. */
const UndoPanel: React.FC<{
  tenantId: string;
  tenantNombre: string;
  lote: LoteInfo;
  onDone: () => void;
  onCancel: () => void;
}> = ({ tenantId, tenantNombre, lote, onDone, onCancel }) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const [preview, setPreview] = useState<UndoPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState('');
  const [running, setRunning] = useState(false);
  const [blocks, setBlocks] = useState<UndoBlock[] | null>(null);

  React.useEffect(() => {
    getUndoPreview(tenantId, lote.guid)
      .then(setPreview)
      .catch((err) => showError(errorText(err)))
      .finally(() => setLoading(false));
  }, [tenantId, lote.guid, showError]);

  const run = async () => {
    setRunning(true);
    try {
      const result = await undoMigration(tenantId, lote.guid, confirm);
      if (!result.ok) {
        setBlocks(result.bloqueos);
        return;
      }
      success(i18next.t(`${K}.doneToast`));
      onDone();
    } catch (err) {
      showError(errorText(err));
    } finally {
      setRunning(false);
    }
  };

  const bloqueos = blocks ?? preview?.bloqueos ?? [];
  const conteos = preview?.conteos ?? {};
  const rows: Array<[string, number]> = [
    ['Paciente', conteos.Paciente ?? 0],
    ['Turno', conteos.Turno ?? 0],
    ['MovimientoCaja', conteos.MovimientoCaja ?? 0],
    ['VencimientoSaldoPaciente', conteos.VencimientoSaldoPaciente ?? 0],
    ['PlanPorPaciente', conteos.PlanPorPaciente ?? 0],
    ['AgendaProfesional', conteos.AgendaProfesional ?? 0],
    ['PacienteModificado', conteos.PacienteModificado ?? 0],
  ];
  const nameOk = confirm.trim().toLowerCase() === tenantNombre.trim().toLowerCase();

  return (
    <div className="mt-3 rounded-xl border border-rose-200 p-3 dark:border-rose-800 sm:p-4">
      {loading ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{t(`${K}.loading`)}</p>
      ) : (
        <>
          <p className="text-sm text-slate-700 dark:text-slate-200">{t(`${K}.willDelete`)}</p>
          <ul className="mt-2 grid grid-cols-1 gap-1 text-sm sm:grid-cols-2">
            {rows
              .filter(([, n]) => n > 0)
              .map(([table, n]) => (
                <li key={table} className="text-slate-700 dark:text-slate-200">
                  {t(`${K}.tables.${table}`, { count: n })}
                </li>
              ))}
          </ul>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t(`${K}.onlyThis`)}</p>

          {bloqueos.length > 0 ? (
            <BlockList bloqueos={bloqueos} />
          ) : (
            <div className="mt-3">
              <label className="block text-xs text-slate-600 dark:text-slate-300" htmlFor={`undo-${lote.guid}`}>
                {t(`${K}.typeName`, { clinic: tenantNombre })}
              </label>
              <input
                id={`undo-${lote.guid}`}
                className="panel-focus mt-1 w-full max-w-sm rounded-md border border-slate-200 px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800"
                value={confirm}
                autoComplete="off"
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
          )}
        </>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {!loading && bloqueos.length === 0 && (
          <Button variant="danger" size="sm" onClick={run} isLoading={running} disabled={!nameOk}>
            {t(`${K}.confirm`)}
          </Button>
        )}
        <Button variant="secondary" size="sm" onClick={onCancel} disabled={running}>
          {t(`${K}.cancel`)}
        </Button>
      </div>
    </div>
  );
};

/** Migraciones aplicadas en la clínica, con la opción de deshacer la última. */
export const ClinicMigrations: React.FC<{
  tenantId: string;
  tenantNombre: string;
  lotes: LoteInfo[];
  onChanged: () => void;
}> = ({ tenantId, tenantNombre, lotes, onChanged }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState<string | null>(null);
  if (!lotes.length) return null;

  return (
    <div className="panel-card p-4 sm:p-5">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t(`${K}.title`)}</h3>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t(`${K}.hint`)}</p>
      <ul className="mt-3 divide-y divide-slate-100 dark:divide-gray-700">
        {lotes.map((l) => (
          <li key={l.guid} className="py-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0 text-sm">
                <span className="font-medium text-slate-800 dark:text-slate-100">
                  {l.consultorioNombre ?? `#${l.consultorioId}`}
                </span>
                <span className="text-slate-500 dark:text-slate-400">
                  {' — '}
                  {t(`${K}.appliedOn`, { date: when(l.fecha), user: l.usuario ?? '—' })}
                </span>
                {l.estado === 'DESHECHA' && (
                  <span className="panel-chip ml-2 bg-slate-100 text-slate-600 dark:bg-gray-800 dark:text-slate-300">
                    {t(`${K}.undoneChip`, { date: l.deshechaEn ? when(l.deshechaEn) : '' })}
                  </span>
                )}
              </div>
              {l.estado === 'APLICADA' && l.esUltima && open !== l.guid && (
                <Button variant="secondary" size="sm" onClick={() => setOpen(l.guid)}>
                  {t(`${K}.button`)}
                </Button>
              )}
              {l.estado === 'APLICADA' && !l.esUltima && (
                <span className="text-xs text-slate-500 dark:text-slate-400">{t(`${K}.notLast`)}</span>
              )}
            </div>
            {open === l.guid && (
              <UndoPanel
                tenantId={tenantId}
                tenantNombre={tenantNombre}
                lote={l}
                onCancel={() => setOpen(null)}
                onDone={() => {
                  setOpen(null);
                  onChanged();
                }}
              />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
